import Client, { LeadNote } from '@spike/client-model';
import { ClientsStatus, deleteLeadNoteThunk, loadLeadNotesThunk, saveLeadNoteThunk } from '@spike/clients-action';
import { useNonInitialEffect } from '@versiondos/hooks';
import { ConfirmDialog } from 'components/UI';
import { Pagination } from 'components/UI/V3/Pagination/Pagination';
import { useApiClientWrapper, useTimeZone } from 'hooks';
import moment from 'moment-timezone';
import { FunctionComponent, MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import LeadNoteComposer from './LeadNoteComposer';
import LeadNotesList from './LeadNotesList';
import styles from './LeadNotesTab.module.scss';

interface LeadNotesTabProps {
    lead: Client;
    onNoteAdded?: () => void;
    seededNotesLeadIdRef: MutableRefObject<number | undefined>;
}

const PAGE_SIZE_OPTIONS = [10, 25, 50];

const LeadNotesTab: FunctionComponent<LeadNotesTabProps> = ({ lead, onNoteAdded, seededNotesLeadIdRef }) => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const timeZone = useTimeZone();

    const notes = useSelector<RootState, Array<LeadNote>>(state => state.clients.leadNotes ?? []);
    const clientsStatus = useSelector<RootState, ClientsStatus>(state => state.clients.status);

    const [draft, setDraft] = useState('');
    const [editingNote, setEditingNote] = useState<LeadNote | null>(null);
    const [deletingNote, setDeletingNote] = useState<LeadNote | null>(null);
    const [page, setPage] = useState(1);
    const [size, setSize] = useState(PAGE_SIZE_OPTIONS[0]);
    const [creating, setCreating] = useState(false);

    const composerRef = useRef<HTMLTextAreaElement>(null);

    const saving = clientsStatus === ClientsStatus.LeadNotesSaving;
    const deleting = clientsStatus === ClientsStatus.LeadNotesDeleting;

    useEffect(() => {
        if (seededNotesLeadIdRef.current === lead.id) {
            return;
        }

        seededNotesLeadIdRef.current = lead.id;
        dispatch(loadLeadNotesThunk(lead.leadNotes ?? []));
    }, [dispatch, lead.id, lead.leadNotes, seededNotesLeadIdRef]);

    const sortedNotes = useMemo(
        () => [...notes].sort((a, b) => moment(b.createdAt).valueOf() - moment(a.createdAt).valueOf()),
        [notes]
    );

    const totalPages = Math.max(1, Math.ceil(sortedNotes.length / size));
    const currentPage = Math.min(page, totalPages);
    const visibleNotes = useMemo(
        () => sortedNotes.slice((currentPage - 1) * size, currentPage * size),
        [sortedNotes, currentPage, size]
    );

    const resetComposer = useCallback(() => {
        setDraft('');
        setEditingNote(null);
    }, []);

    useNonInitialEffect(() => {
        if (clientsStatus !== ClientsStatus.LeadNotesSaveSuccess) {
            return;
        }

        resetComposer();

        if (creating) {
            setCreating(false);
            setPage(1);
            onNoteAdded?.();
        }
    }, [clientsStatus]);

    useNonInitialEffect(() => {
        if (clientsStatus === ClientsStatus.LeadNotesDeleteSuccess) {
            setDeletingNote(null);
        }
    }, [clientsStatus]);

    useNonInitialEffect(() => {
        if (clientsStatus === ClientsStatus.LeadNotesError) {
            setCreating(false);
        }
    }, [clientsStatus]);

    const handleSubmit = useCallback(() => {
        const message = draft.trim();

        if (message.length === 0 || lead.id === undefined || saving) {
            return;
        }

        setCreating(editingNote === null);
        dispatch(saveLeadNoteThunk(apiClientWrapper, lead.id, { id: editingNote?.id, message }));
    }, [apiClientWrapper, dispatch, draft, editingNote, lead.id, saving]);

    const handleEdit = useCallback((note: LeadNote) => {
        setEditingNote(note);
        setDraft(note.content);
        composerRef.current?.focus({ preventScroll: true });
        composerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, []);

    const confirmDelete = useCallback(() => {
        if (!deletingNote || lead.id === undefined || deleting) {
            return;
        }

        if (editingNote?.id === deletingNote.id) {
            resetComposer();
        }

        dispatch(deleteLeadNoteThunk(apiClientWrapper, lead.id, deletingNote.id));
    }, [apiClientWrapper, deleting, deletingNote, dispatch, editingNote, lead.id, resetComposer]);

    return (
        <div className={styles.root}>
            <div className={styles.heading}>
                <h3 className={styles.title}>Notes</h3>
                <p className={styles.subtitle}>Enter important notes about this lead.</p>
            </div>

            <LeadNoteComposer
                ref={composerRef}
                value={draft}
                isEditing={editingNote !== null}
                isSaving={saving}
                onChange={setDraft}
                onSubmit={handleSubmit}
                onCancel={resetComposer}
            />

            <hr className={styles.divider} />

            {sortedNotes.length === 0 ? (
                <p className={styles.empty}>There are no notes for this lead.</p>
            ) : (
                <>
                    <LeadNotesList
                        notes={visibleNotes}
                        timeZone={timeZone}
                        onEdit={handleEdit}
                        onDelete={setDeletingNote}
                    />

                    <Pagination
                        page={currentPage}
                        totalPages={totalPages}
                        size={size}
                        onPageChange={setPage}
                        onSizeChange={newSize => {
                            setSize(newSize);
                            setPage(1);
                        }}
                    />
                </>
            )}

            <ConfirmDialog
                open={deletingNote !== null}
                question={<span>Are you sure you want to delete this note?</span>}
                confirmButtonLabel="Delete"
                cancelButtonLabel="Cancel"
                processing={deleting}
                onCancel={() => setDeletingNote(null)}
                onConfirm={confirmDelete}
            />
        </div>
    );
};

export default LeadNotesTab;
